import { getBaseAppPath } from "../../configs";
import { getAbsoluteBackendEndpoint } from "../../constants/backendRoutes";
import { RECAPTCHA_CHALLENGE_CODE } from "../../constants/recaptcha";
import UseAuthService from "../../services/auth.service";
import UseRecaptchaService from "../../services/recaptcha.service";
import { CONSUME_OTP_CODE_ERRORS } from "./auth.constants";

declare global {
  interface Window {
    recaptchaToken?: string;
    recaptchaChallengeToken?: string;
  }
}

const authService = new UseAuthService();

export const authController = {
  /**
   * Sends the passwordless OTP code, gated by reCAPTCHA.
   *
   * First pass: called with the invisible v3 `recaptchaToken`. If the backend
   * decides a visible puzzle is required it throws with `cause: "challenge"`, so
   * the caller can render the checkbox and retry.
   *
   * Second pass (puzzle solved): called with `challengeToken` and no
   * `recaptchaToken`.
   */
  async sendOTPCode(
    email?: string,
    recaptchaToken?: string,
    challengeToken?: string
  ) {
    if (!email) {
      throw new Error("Email is required", { cause: "email" });
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      throw new Error("Invalid email format", { cause: "email" });
    }

    if (
      UseRecaptchaService.isEnabled() &&
      !recaptchaToken &&
      !challengeToken
    ) {
      throw new Error("reCAPTCHA verification is required");
    }

    window.recaptchaToken = recaptchaToken;
    window.recaptchaChallengeToken = challengeToken;

    try {
      const createCodeResult = await authService.createCode({
        email,
      });

      if (createCodeResult?.status !== "OK") {
        throw new Error(createCodeResult.reason);
      }
    } catch (error: unknown) {
      // SuperTokens throws STGeneralError when the backend returns
      // { status: "GENERAL_ERROR" } — that's how our reCAPTCHA challenge
      // signal arrives, so detect it here (not on the returned result).
      if (
        error instanceof Error &&
        (error as { isSuperTokensGeneralError?: boolean })
          .isSuperTokensGeneralError &&
        error.message === RECAPTCHA_CHALLENGE_CODE
      ) {
        throw new Error(error.message, { cause: "challenge" });
      }

      console.error({ error });

      if ((error as Response)?.status === 401) {
        await this._handleUnauthorizedError();

        return;
      }

      throw error;
    } finally {
      // Avoid reusing a stale token on a later attempt.
      window.recaptchaToken = undefined;
      window.recaptchaChallengeToken = undefined;
    }
  },

  async consumeCode(otpCode: string | undefined) {
    try {
      if (!otpCode) {
        throw new Error("OTP code is required", { cause: "otpCode" });
      }

      const consumeCodeResult = await authService.consumeCode({
        userInputCode: otpCode,
      });

      const { status } = consumeCodeResult;

      if (status !== "OK") {
        let errorMessage = CONSUME_OTP_CODE_ERRORS[status] || "Unknown error";
        let newConsumeCodeAttemptsLeft = undefined;

        if (status === "INCORRECT_USER_INPUT_CODE_ERROR") {
          const { maximumCodeInputAttempts, failedCodeInputAttemptCount } =
            consumeCodeResult;
          newConsumeCodeAttemptsLeft =
            maximumCodeInputAttempts - failedCodeInputAttemptCount;

          if (
            !authController.validateResendAttempts(newConsumeCodeAttemptsLeft)
          ) {
            throw new Error(
              "You have exceeded the maximum number of attempts. Please try again later",
              { cause: { newConsumeCodeAttemptsLeft } }
            );
          }

          const isLessThan3Attempts = newConsumeCodeAttemptsLeft <= 3;

          if (isLessThan3Attempts) {
            errorMessage += `\nYou have ${newConsumeCodeAttemptsLeft} attempts left.`;

            throw new Error(errorMessage, {
              cause: { newConsumeCodeAttemptsLeft },
            });
          }
        }

        throw new Error(errorMessage);
      }

      document.location.href = getAbsoluteBackendEndpoint("REDIRECT_FOLLOW");
    } catch (error) {
      console.error(error);

      if ((error as Response)?.status === 401) {
        this._handleUnauthorizedError();

        return;
      }

      throw error;
    }
  },

  async consumeLinkCode(
    linkCode: string | null,
    preAuthSessionId: string | null
  ) {
    try {
      if (!linkCode) {
        throw new Error("linkCode is required");
      }

      if (!preAuthSessionId) {
        throw new Error("preAuthSessionId is required");
      }

      await authService.consumeLinkCode(linkCode, preAuthSessionId);

      document.location.href = getAbsoluteBackendEndpoint("REDIRECT_FOLLOW");
    } catch (error) {
      console.error(error);

      if ((error as Response)?.status === 401) {
        await this._handleUnauthorizedError();

        return;
      }

      throw new Error("Error consuming link code");
    }
  },

  async _handleUnauthorizedError() {
    await authService.signOut();

    document.location.href = getBaseAppPath();
  },

  async resendCode(email?: string) {
    try {
      if (!email) {
        throw new Error("Email is required");
      }

      await authService.resendCode();
    } catch (error) {
      console.error(error);

      if ((error as Response)?.status === 401) {
        await this._handleUnauthorizedError();

        return;
      }

      throw error;
    }
  },

  validateResendAttempts(resendAttempts: number) {
    if (resendAttempts <= 0) {
      throw new Error(
        "You have exceeded the maximum number of attempts. Please try again later"
      );
    }

    return true;
  },

  async refreshSession(cancel: boolean) {
    try {
      const session = await authService.doesSessionExist();

      if (session) {
        const success = await authService.attemptRefreshingSession();

        if (!success) {
          throw new Error("Session expired");
        }

        document.location.href = getAbsoluteBackendEndpoint("REDIRECT_FOLLOW");
      }

      if (cancel) {
        // component has unmounted somehow..
        return;
      }
    } catch (error) {
      console.error(error);
      authService.signOut();
    }
  },
};
